import { useMemo } from 'react'
import { ReactComponent as SocialListeningIcon } from 'src/App/AppRouter/Layout/Sidebar/icons/Social-Listening.svg'
import { ReactComponent as InboxAnalysisIcon } from 'src/App/AppRouter/Layout/Sidebar/icons/Inbox-Analysis.svg'
import { ReactComponent as ContentAnalysisIcon } from 'src/App/AppRouter/Layout/Sidebar/icons/Content-Analysis.svg'
import { ReactComponent as ReviewAnalysisIcon } from 'src/App/AppRouter/Layout/Sidebar/icons/Review-Analysis.svg'
import { ReactComponent as AdAnalysisIcon } from 'src/App/AppRouter/Layout/Sidebar/icons/Ad-Analysis.svg'
import { ReactComponent as AIListeningIcon } from 'src/assets/svg/ai-Listening.svg'
import { useGetAiListeningCampaignsQuery } from 'src/reactQuery/useGetAiListeningCampaignsQuery'
import { useGetCampaignsQuery } from 'src/reactQuery/useGetCampaignsQuery'
import { useGetContentAnalysisListQuery } from 'src/reactQuery/useGetContentAnalysisListQuery'
import { useGetInboxAnalysisPagesQuery } from 'src/reactQuery/useGetInboxAnalysisPagesQuery'
import { useGetMonitoringPagesQuery } from 'src/reactQuery/useGetMonitoringPagesQuery'
import { useGetReviewAnalysisCampaignsQuery } from 'src/reactQuery/useGetReviewAnalysisCampaignsQuery'
import { useGetReviewCampaignFacilities } from 'src/reactQuery/useGetReviewCampaignFacilities'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import {
  CampaignItem,
  CampaignSourceRow,
  SourceId,
} from './interfaces/campaignSource.interface'

interface UseCampaignSourcesParams {
  enabled: boolean
}

export const formatCompactNumber = (value: number) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    notation: 'compact',
  }).format(value)

export const createSelectableId = (sourceId: SourceId, backendId: string) =>
  `${sourceId}:${backendId}`

const getCampaignUnitNoun = (sourceId: SourceId) => {
  switch (sourceId) {
    case 'inbox-analysis':
      return 'message'
    case 'content-analysis':
      return 'post'
    case 'review-analysis':
    case 'ad-analysis':
      return 'review'
    default:
      return 'mention'
  }
}

// Pluralize the unit based on the raw count: "1 mention" vs "2 mentions".
export const getCampaignUnitLabel = (sourceId: SourceId, count: number) => {
  const noun = getCampaignUnitNoun(sourceId)
  return `${noun}${count === 1 ? '' : 's'}`
}

// For review-analysis each row is a facility (location); for every other source
// each row is a campaign/entity. The displayed count is just the row count.
export const getSourceEntityLabel = (sourceId: SourceId, count: number) => {
  const noun = sourceId === 'review-analysis' ? 'location' : 'campaign'
  return `${noun}${count === 1 ? '' : 's'}`
}

export const useCampaignSources = ({ enabled }: UseCampaignSourcesParams) => {
  const { data: socialCampaigns } = useGetCampaignsQuery({})
  const { data: aiListeningCampaigns } = useGetAiListeningCampaignsQuery()
  const { data: userFeatures } = useGetUserFeaturesQuery()
  // The host modal is mounted (closed) on every page visit; only hit the
  // feature-gated inbox endpoint when it is open AND the user has the feature,
  // otherwise every user without inbox analysis produces a 403 per page view.
  const { data: inboxPages } = useGetInboxAnalysisPagesQuery(
    {},
    {
      enabled: enabled && (userFeatures?.data?.inboxAnalysisEnabled ?? false),
    }
  )
  const { data: contentAnalysisList } = useGetContentAnalysisListQuery()
  const { data: reviewCampaigns } = useGetReviewAnalysisCampaignsQuery()
  const { data: monitoringPages } = useGetMonitoringPagesQuery()
  const reviewCampaignIds = useMemo(
    () => (reviewCampaigns || []).map((campaign) => campaign._id),
    [reviewCampaigns]
  )
  const reviewFacilities = useGetReviewCampaignFacilities({
    campaignIds: reviewCampaignIds,
    enabled,
  })

  return useMemo<CampaignSourceRow[]>(() => {
    const socialCampaignRows: CampaignItem[] =
      socialCampaigns?.campaigns?.map((campaign) => ({
        backendId: campaign._id || '',
        category:
          campaign.targets?.join(', ') || campaign.status || 'Social Listening',
        id: createSelectableId('social-listening', campaign._id || ''),
        mentions: formatCompactNumber(campaign.stats?.mentions || 0),
        mentionsCount: campaign.stats?.mentions || 0,
        sourceId: 'social-listening',
        title: campaign.name || 'Untitled campaign',
      })) || []

    const aiCampaignRows =
      aiListeningCampaigns?.data?.map((campaign) => ({
        backendId: campaign._id || '',
        category: campaign.brandName || campaign.status || 'AI Listening',
        id: createSelectableId('ai-listening', campaign._id || ''),
        mentions: formatCompactNumber(campaign.topInsight ? 1 : 0),
        mentionsCount: campaign.topInsight ? 1 : 0,
        sourceId: 'ai-listening' as const,
        title: campaign.name || 'Untitled campaign',
      })) || []

    const inboxPageRows =
      inboxPages?.pages?.map((page) => ({
        backendId: page.id,
        category: page.platforms?.join(', ') || 'Inbox Analysis',
        id: createSelectableId('inbox-analysis', page.id),
        mentions: formatCompactNumber(page.messages?.total || 0),
        mentionsCount: page.messages?.total || 0,
        sourceId: 'inbox-analysis' as const,
        title: page.name,
      })) || []

    const contentAnalysisRows =
      contentAnalysisList?.data?.map((analysis) => ({
        backendId: analysis._id,
        category:
          analysis.categories?.join(', ') ||
          analysis.status ||
          'Content Analysis',
        id: createSelectableId('content-analysis', analysis._id),
        mentions: formatCompactNumber(analysis.totalPosts || 0),
        mentionsCount: analysis.totalPosts || 0,
        sourceId: 'content-analysis' as const,
        title: analysis.title,
      })) || []

    // One row per facility (location), flattened across all review campaigns.
    const reviewCampaignRows: CampaignItem[] = (reviewCampaigns || []).flatMap(
      (campaign) => {
        const facilities = reviewFacilities[campaign._id] || []
        return facilities.map((facility) => {
          const platforms = Array.from(
            new Set((facility.cards || []).map((card) => card.platform))
          )
          const reviewCount = (facility.cards || []).reduce(
            (sum, card) => sum + (card.reviewCount || 0),
            0
          )
          const backendId = `${campaign._id}:${facility.id}`
          return {
            backendId,
            campaignId: campaign._id,
            category: platforms.join(', ') || 'Review Analysis',
            facilityId: facility.id,
            id: createSelectableId('review-analysis', backendId),
            mentions: formatCompactNumber(reviewCount),
            mentionsCount: reviewCount,
            sourceId: 'review-analysis' as const,
            title: facility.name,
          }
        })
      }
    )

    const adAnalysisRows =
      monitoringPages?.data?.map((page) => ({
        backendId: page.page_id || '',
        category: page.category || 'Ad Analysis',
        id: createSelectableId('ad-analysis', page.page_id || ''),
        mentions: formatCompactNumber(page.latest_snapshot?.active_count || 0),
        mentionsCount: page.latest_snapshot?.active_count || 0,
        sourceId: 'ad-analysis' as const,
        title: page.page_name || page.page_id || 'Ad page',
      })) || []

    return [
      {
        campaigns: socialCampaignRows,
        color: '#2563EB',
        icon: SocialListeningIcon,
        id: 'social-listening',
        label: 'Social Listening',
      },
      {
        campaigns: aiCampaignRows,
        color: '#7C3AED',
        icon: AIListeningIcon,
        id: 'ai-listening',
        label: 'AI Listening',
      },
      {
        campaigns: inboxPageRows,
        color: '#0891B2',
        icon: InboxAnalysisIcon,
        id: 'inbox-analysis',
        label: 'Inbox Analysis',
      },
      {
        campaigns: contentAnalysisRows,
        color: '#059669',
        icon: ContentAnalysisIcon,
        id: 'content-analysis',
        label: 'Content Analysis',
      },
      {
        campaigns: reviewCampaignRows,
        color: '#D97706',
        icon: ReviewAnalysisIcon,
        id: 'review-analysis',
        label: 'Review Analysis',
      },
      {
        campaigns: adAnalysisRows,
        color: '#2563EB',
        icon: AdAnalysisIcon,
        id: 'ad-analysis',
        label: 'Ad Analysis',
      },
    ]
  }, [
    aiListeningCampaigns?.data,
    contentAnalysisList?.data,
    inboxPages?.pages,
    monitoringPages?.data,
    reviewCampaigns,
    reviewFacilities,
    socialCampaigns?.campaigns,
  ])
}
